import type { User } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { Cubit } from '../cubits/core'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { getUserProfilePurchases } from '../firestore/UserProfilePurchase'
import { getUserProfileTokens } from '../firestore/UserProfileToken'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { UserProfilePurchase } from '../models/UserProfilePurchase'
import { UserProfileToken } from '../models/UserProfileToken'
import { fetchSlideDeck } from '../firestore/SlideDeck'
import type { SlideDeck } from '../models/SlideDeck'
import { UserProfileSectionPass } from '../models/UserProfileSectionPass'
import { getSectionPassesAndRebates } from '../firestore/UserProfileSectionPass'
import { DateTime } from 'luxon'
import { UserProfileSectionPassRebate } from '../models/UserProfileSectionPassRebate'

export class UserProfileTokens extends Cubit {
  firestore: Firestore
  repository: FirebaseRepository
  user: BreakoutUser | User

  _tokensCollection: StaticModelCollection<UserProfileToken>
  _purchaseCollection: StaticModelCollection<UserProfilePurchase>
  _sectionPassCollection: StaticModelCollection<UserProfileSectionPass>
  _sectionPassRebateCollection: StaticModelCollection<UserProfileSectionPassRebate>

  @observable
  private _mappedSlideDecksToConsumedTokens:
    | Record<string, SlideDeck>
    | undefined = undefined

  @observable
  private _now = DateTime.now()

  private _updateNowTimeout: NodeJS.Timeout | undefined = undefined

  constructor(repository: FirebaseRepository, user: BreakoutUser | User) {
    super()
    makeObservable(this)
    this.user = user
    this.repository = repository
    this.firestore = repository.firestore
    this._tokensCollection = UserProfileToken.emptyCollection(repository)
    this._purchaseCollection = UserProfilePurchase.emptyCollection(repository)
    this._sectionPassCollection =
      UserProfileSectionPass.emptyCollection(repository)
    this._sectionPassRebateCollection =
      UserProfileSectionPassRebate.emptyCollection(repository)

    this.disposers.push(() => clearTimeout(this._updateNowTimeout))
  }

  startTokenStreamIfNotStarted() {
    const name = 'tokens'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getUserProfileTokens(this.repository, { userId: this.user.uid }),
      (tokens) => {
        // set date to now and clear timeout for the next tokensAvailable compute
        runInAction(() => (this._now = DateTime.now()))
        clearTimeout(this._updateNowTimeout)

        this._tokensCollection.replaceModels(tokens)
      },
      { name }
    )
  }

  startPurchasesStreamIfNotStarted() {
    const name = 'purchases'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getUserProfilePurchases(this.repository, { userId: this.user.uid }),
      (purchases) => {
        this._purchaseCollection.replaceModels(purchases)
      },
      { name }
    )
  }

  startSectionPassesStreamIfNotStarted() {
    const name = 'sectionPasses'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getSectionPassesAndRebates(this.repository, { userId: this.user.uid }),
      ({ rebates, passes }) => {
        this._sectionPassCollection.replaceModels(passes)
        this._sectionPassRebateCollection.replaceModels(rebates)
      },
      { name }
    )
  }

  async fetchConsumedTokenSlideDecks() {
    const consumedTokens = this.tokens.filter((token) => token.data.consumed)

    const slideDecksMappedToTokens: Record<string, SlideDeck> = {}

    await Promise.all(
      consumedTokens.map(async (token) => {
        if (!token.data.slideDeckId) return

        const deck = await fetchSlideDeck(this.repository, {
          slideDeckId: token.data.slideDeckId,
        })
        runInAction(() => (slideDecksMappedToTokens[token.id] = deck))
      })
    )

    runInAction(
      () => (this._mappedSlideDecksToConsumedTokens = slideDecksMappedToTokens)
    )
  }

  @computed
  get mappedSlideDecksToConsumedTokens() {
    if (
      typeof this._mappedSlideDecksToConsumedTokens === 'undefined' &&
      this.tokensCollection.length > 0
    ) {
      // We can safely assume that the slide decks have not been fetched yet.
      this.fetchConsumedTokenSlideDecks()
    }

    return this._mappedSlideDecksToConsumedTokens
  }

  get tokensCollection() {
    this.startTokenStreamIfNotStarted()
    return this._tokensCollection
  }

  get purchaseCollection() {
    this.startPurchasesStreamIfNotStarted()
    return this._purchaseCollection
  }

  get sectionPassesCollection() {
    this.startSectionPassesStreamIfNotStarted()
    return this._sectionPassCollection
  }

  @computed
  get isLoading() {
    return (
      this.tokensCollection.isLoading ||
      this.purchaseCollection.isLoading ||
      this.sectionPassesCollection.isLoading
    )
  }

  @computed
  private get _availableAndExpiredTokens() {
    let lowestExpiresInMS = Number.POSITIVE_INFINITY

    const availableCount = this.tokens.reduce(
      (acc, token) => {
        const { eligible, expiresInMS } = token.isEligibleForConsumption(
          this._now
        )

        if (eligible && expiresInMS < lowestExpiresInMS)
          lowestExpiresInMS = expiresInMS

        if (eligible) {
          acc.available += token.data.tokenQuantity
        } else if (expiresInMS <= 0 && !token.data.consumed) {
          acc.expired += token.data.tokenQuantity
        }

        return acc
      },
      { available: 0, expired: 0 }
    )

    this.setUpdateNowTimeout(lowestExpiresInMS)

    return availableCount
  }

  @computed
  get availableTokens() {
    return this._availableAndExpiredTokens.available
  }

  @computed
  get expiredTokens() {
    return this._availableAndExpiredTokens.expired
  }

  @computed
  get consumedTokens() {
    return this.tokens
      .filter((token) => token.data.consumed === true)
      .reduce((acc, token) => acc + token.data.tokenQuantity, 0)
  }

  @computed
  private get _sectionPassRebateBalances() {
    this.startSectionPassesStreamIfNotStarted()
    return this._sectionPassRebateCollection.models.reduce(
      (acc, { data }) => {
        const { consumedAt, price } = data
        if (consumedAt) acc.consumed += price
        else acc.available += price
        return acc
      },
      { available: 0, consumed: 0 }
    )
  }

  @computed
  get sectionPassRebateAvailableCents() {
    return this._sectionPassRebateBalances.available
  }

  @computed
  get sectionPassRebateConsumedCents() {
    return this._sectionPassRebateBalances.consumed
  }

  get tokens() {
    return this.tokensCollection.models
  }

  get purchases() {
    return this.purchaseCollection.models
  }

  get sectionPassSectionIds() {
    return new Set(this._sectionPassCollection.models.map((model) => model.id))
  }

  private setUpdateNowTimeout(expiresInMS: number) {
    clearTimeout(this._updateNowTimeout)

    // if no eligible tokens with expiry, no timeout is required
    if (!Number.isFinite(expiresInMS) || expiresInMS <= 0) return

    // clamp timeout to max supported values (max int32) if expiresInMS is too large
    const timeoutMs = Math.min(expiresInMS + 1, 2_147_483_647)

    // set a timeout to update the _now property, which will re-trigger tokensAvailable
    // and correctly set the token to to unavailable if expired
    this._updateNowTimeout = setTimeout(() => {
      runInAction(() => (this._now = DateTime.now()))
    }, timeoutMs) // add 1ms for safety
  }
}
